import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useGlobalOrderData } from '../../../hooks/useGlobalOrderData';
import { useAuth } from '../../../contexts/AuthContext';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../../styles/ThemeStyles';
import { PDFService } from '../../../services/PDFService';
import { EmailService } from '../../../services/EmailService';
import { getTranslatedProductName } from '../../../utils/translationHelpers';
import { formatOrderDateForDisplay, getLocalCalendarYMD } from '../../../utils/calendarDate';
import { OrderFinalizationService } from '../../../services/OrderFinalizationService';
import { 
  FileText, 
  Mail, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft,
  Send,
  Download,
  Edit,
  Save,
  X,
  PenTool,
  Trash2,
  Home,
  Store
} from 'lucide-react';
import { Breadcrumb } from '../../../components/common';

const PreviewContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: ${Spacing.xl};
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.lg};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;


const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  padding: ${Spacing.lg};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  border-radius: ${BorderRadius.lg};
  color: white;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    gap: ${Spacing.md};
    margin-bottom: ${Spacing.md};
    padding: ${Spacing.md};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.1) rotate(180deg); }
  }
`;

const PreviewHeaderContent = styled.div`
  position: relative;
  z-index: 1;
`;

const PreviewTitle = styled.h2`
  font-size: ${Typography.fontSize.xxl};
  font-family: ${Typography.fontFamily.secondary};
  font-weight: ${Typography.fontWeight.bold};
  color: white;
  margin: 0 0 ${Spacing.sm} 0;
  letter-spacing: -0.02em;
`;

const PreviewSubtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  opacity: 0.9;
`;

const PDFPreviewContainer = styled.div`
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  margin-bottom: ${Spacing.xl};
  overflow: hidden;
`;

const PDFHeader = styled.div`
  background: ${ThemeColors.primary.main};
  color: white;
  padding: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const PDFContent = styled.div`
  padding: ${Spacing.lg};
  background: white;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
`;

const StaticPDFPreview = styled.div`
  padding: ${Spacing.xl};
  background: white;
  font-family: 'Helvetica', 'Arial', sans-serif;
  color: #000;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
    line-height: 1.3;
  }
  
  /* PDF-like styling */
  @media print {
    page-break-after: always;
  }
`;

const PDFPreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.lg};
  padding-bottom: ${Spacing.md};
  border-bottom: 2px solid ${ThemeColors.primary.main};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
    padding-bottom: ${Spacing.sm};
  }
`;

const PDFPreviewLogo = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.main};
`;

const PDFPreviewCompanyInfo = styled.div`
  text-align: right;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const PDFPreviewTitle = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.lg} 0;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.xl};
    margin: ${Spacing.md} 0;
    line-height: 1.2;
  }
`;

const PDFPreviewSection = styled.div`
  margin-bottom: ${Spacing.xl};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
  }
`;

const PDFPreviewSectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  padding-bottom: ${Spacing.xs};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.md};
    margin-bottom: ${Spacing.sm};
  }
`;

const PDFPreviewTwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.md};
    margin-bottom: ${Spacing.md};
  }
`;

const PDFPreviewInfoItem = styled.div`
  margin-bottom: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.xs};
    font-size: ${Typography.fontSize.xs};
  }
`;

const PDFPreviewInfoValue = styled.div<{ isEditable?: boolean }>`
  ${props => props.isEditable ? `
    input {
      width: 100%;
      padding: ${Spacing.xs} ${Spacing.sm};
      border: 1px solid ${ThemeColors.border.light};
      border-radius: ${BorderRadius.sm};
      font-size: ${Typography.fontSize.sm};
      background: white;
      
      &:focus {
        outline: none;
        border-color: ${ThemeColors.primary.main};
        box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
      }
    }
  ` : ''}
`;

const PDFPreviewInfoLabel = styled.span`
  font-weight: ${Typography.fontWeight.bold};
  margin-right: ${Spacing.xs};
`;

const EditableInput = styled.input`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background: white;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.xs};
    padding: ${Spacing.xs};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background: white;
  min-height: 60px;
  resize: vertical;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.xs};
    padding: ${Spacing.xs};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const EditableSelect = styled.select`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background: white;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.xs};
    padding: ${Spacing.xs};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const EditableQuantityInput = styled.input`
  width: 60px;
  padding: ${Spacing.xs};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: 16px; /* Prevent zoom on iOS - must be at least 16px */
  text-align: center;
  background: white;
  
  @media (max-width: 768px) {
    width: 50px;
    font-size: 16px; /* Keep 16px on mobile to prevent zoom */
    padding: 2px;
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const PDFDateInfo = styled.div`
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.sm};
    font-size: ${Typography.fontSize.xs};
  }
`;

const ProductSectionContainer = styled.div`
  margin-bottom: ${Spacing.lg};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
  }
`;

const ProductSectionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  margin-bottom: ${Spacing.sm};
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.sm};
    margin-bottom: ${Spacing.xs};
  }
`;

const PDFPreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${Spacing.lg};
`;

const PDFPreviewTableHeader = styled.thead`
  background: ${ThemeColors.gray[100]};
  
  th {
    padding: ${Spacing.sm};
    text-align: left;
    font-weight: ${Typography.fontWeight.bold};
    font-size: ${Typography.fontSize.sm};
    border-bottom: 1px solid ${ThemeColors.border.light};
    
    @media (max-width: 768px) {
      padding: ${Spacing.xs} ${Spacing.sm};
      font-size: ${Typography.fontSize.xs};
    }
  }
`;

const PDFPreviewTableBody = styled.tbody`
  tr:nth-child(even) {
    background: ${ThemeColors.gray[50]};
  }
  
  td {
    padding: ${Spacing.sm};
    font-size: ${Typography.fontSize.sm};
    border-bottom: 1px solid ${ThemeColors.border.light};
    
    @media (max-width: 768px) {
      padding: ${Spacing.xs} ${Spacing.sm};
      font-size: ${Typography.fontSize.xs};
      line-height: 1.3;
    }
  }
`;

const PDFPreviewTotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg};
  margin-top: ${Spacing.xl};
  border-top: 2px solid ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
    margin-top: ${Spacing.md};
    font-size: ${Typography.fontSize.md};
  }
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.lg} 0 ${Spacing.md} 0;
  padding-bottom: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:first-child {
    margin-top: 0;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xs};
`;

const InfoValue = styled.span`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.primary};
  padding: ${Spacing.sm};
  background: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProductName = styled.span`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.primary};
`;

const ProductQuantity = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const ProductPrice = styled.span`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg};
  background: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  margin-top: ${Spacing.lg};
  border: 2px solid ${ThemeColors.primary.main};
`;

const TotalLabel = styled.span`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
`;

const TotalAmount = styled.span`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.main};
`;

const EmailSection = styled.div`
  margin-top: ${Spacing.xl};
  padding: ${Spacing.xl};
  background: white;
  border-radius: ${BorderRadius.lg};
  border: 2px solid ${ThemeColors.primary.light};
  box-shadow: ${Shadows.md};
`;

const EmailLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const EmailInput = styled.input`
  width: 100%;
  padding: ${Spacing.lg};
  border: 2px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  margin-bottom: ${Spacing.md};
  background: ${ThemeColors.gray[50]};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
    background: white;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${Spacing.md};
  justify-content: center;
  margin-top: ${Spacing.xl};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    
    button {
      width: 100%;
      max-width: 300px;
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.lg} ${Spacing.xl};
  border: none;
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
  position: relative;
  overflow: hidden;
  
  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    box-shadow: 0 4px 12px rgba(70, 130, 78, 0.3);
    
    &:hover {
      background: ${ThemeColors.primary.dark};
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(70, 130, 78, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 2px solid ${ThemeColors.border.light};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    &:hover {
      background: ${ThemeColors.gray[50]};
      border-color: ${ThemeColors.primary.main};
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.lg};
  border-radius: ${BorderRadius.md};
  margin: ${Spacing.lg} 0;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  text-align: center;
  
  ${props => props.type === 'success' ? `
    background: ${ThemeColors.success.main};
    color: white;
    box-shadow: 0 4px 12px rgba(70, 130, 78, 0.3);
  ` : `
    background: ${ThemeColors.error.light};
    color: ${ThemeColors.error.dark};
    border: 1px solid ${ThemeColors.error.main};
  `}
`;

const SignatureSection = styled.div`
  margin-top: ${Spacing.xl};
  padding: ${Spacing.xl};
  background: white;
  border-radius: ${BorderRadius.lg};
  border: 2px solid ${ThemeColors.primary.light};
  box-shadow: ${Shadows.md};
`;

const SignatureTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const SignatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const SignatureCanvasContainer = styled.div<{ disabled?: boolean }>`
  border: 2px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  background: white;
  padding: ${Spacing.sm};
  position: relative;
  
  canvas {
    width: 100%;
    height: 150px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'crosshair'};
    touch-action: ${props => props.disabled ? 'auto' : 'none'};
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  }
  
  ${props => props.disabled && `
    opacity: 0.8;
  `}
`;

const SignatureButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  justify-content: flex-end;
`;

const SignatureButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover {
      background: ${ThemeColors.primary.dark};
    }
  ` : `
    background: ${ThemeColors.gray[200]};
    color: ${ThemeColors.text.primary};
    
    &:hover {
      background: ${ThemeColors.gray[300]};
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SignatureNote = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  font-style: italic;
  margin: 0;
`;

interface OrderPreviewScreenProps {
  onBack: () => void;
  onFinalSubmit: (email: string) => Promise<void>;
  storeId?: string;
  storeName?: string;
  onNavigate?: (tab: 'store-visit' | 'store-order' | 'store-history') => void;
  /** Clears persisted "preview open" state so returning to the store is not stuck on confirmation */
  onOrderFinalizedSuccess?: () => void;
  /** Manager: return to store details on the Store Visit tab (notes) */
  onExitPreviewToStoreVisit?: () => void;
  /** Type of order - 'store-order' shows only product items, 'supplies' shows only supply items */
  orderType?: 'store-order' | 'supplies';
}

export const OrderPreviewScreen: React.FC<OrderPreviewScreenProps> = ({ 
  onBack, 
  onFinalSubmit,
  storeId,
  storeName,
  onNavigate,
  onOrderFinalizedSuccess,
  onExitPreviewToStoreVisit,
  orderType = 'store-order'
}) => {
  const { t } = useTranslation();
  const { orderData, submitOrderWithEmail, resetOrderData, clearDraft, updateOrderData, saveOrderData } = useGlobalOrderData();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeSuccess, setFinalizeSuccess] = useState(false);
  const finalizationService = new OrderFinalizationService();
  
  // Always reset state when component mounts - this is a new preview
  // Don't auto-detect finalized orders - preview should always start fresh
  useEffect(() => {
    setFinalizeSuccess(false);
    setFinalizedOrderSnapshot(null);
    // Clear signature immediately when opening preview
    updateOrderData({ customerSignature: undefined });
  }, []); // Run only on mount
  
  // Manager edit mode
  const isManager = userRole === 'manager';
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedOrderData, setEditedOrderData] = useState<typeof orderData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Signature canvas ref
  const signatureCanvasRef = useRef<SignatureCanvas | null>(null);
  const signatureContainerRef = useRef<HTMLDivElement>(null);

  // Resize signature canvas to match container width
  useEffect(() => {
    const handleResize = () => {
      if (signatureCanvasRef.current && signatureContainerRef.current) {
        const canvas = signatureCanvasRef.current.getCanvas();
        const container = signatureContainerRef.current;
        
        // Only resize if container width has changed significantly
        // or if canvas width is still the default/wrong
        if (canvas.width !== container.offsetWidth) {
          // Save the current signature if not empty
          const isEmpty = signatureCanvasRef.current.isEmpty();
          const signatureData = !isEmpty ? signatureCanvasRef.current.toDataURL() : null;
          
          // Set canvas resolution to match its display size
          canvas.width = container.offsetWidth;
          canvas.height = 150; // Match CSS height
          
          // Restore signature if there was one
          if (signatureData) {
            signatureCanvasRef.current.fromDataURL(signatureData);
          }
        }
      }
    };

    // Run once on mount after a short delay to ensure container is rendered
    const timer = setTimeout(handleResize, 100);
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Snapshot of order data for static preview (preserved after finalization)
  // This ensures the preview screen shows the finalized order data even after clearing
  const [finalizedOrderSnapshot, setFinalizedOrderSnapshot] = useState<typeof orderData | null>(null);
  
  // Use snapshot for display if available, otherwise use current orderData
  // For managers in edit mode, use editedOrderData
  // Use snapshot for display if available, otherwise use current orderData
  // For managers in edit mode, use editedOrderData
  // Only use finalizedOrderSnapshot if order is actually finalized, otherwise use current orderData
  const displayOrderData = isEditMode && editedOrderData 
    ? editedOrderData 
    : (finalizeSuccess && finalizedOrderSnapshot ? finalizedOrderSnapshot : orderData);

  const previewBreadcrumbItems = useMemo(() => {
    const isManagerUser = userRole === 'manager';
    const dashPath = isManagerUser ? '/manager/dashboard' : '/dashboard';
    const storeVisitListPath = isManagerUser ? '/manager/store-visit' : '/store-visit';
    const goStoreNotes = () => {
      if (onExitPreviewToStoreVisit) {
        onExitPreviewToStoreVisit();
        return;
      }
      if (!storeId) return;
      navigate(
        isManagerUser
          ? `/manager/store-details/${storeId}?tab=store-visit`
          : `/store-details/${storeId}?tab=store-visit`
      );
    };
    const customerNo = (displayOrderData.customerNumber || '').trim();
    const customerCrumbLabel = customerNo
      ? `${t('pdf.customerHash', 'Customer #')} ${customerNo}`
      : t('pdf.customerHash', 'Customer #');
    const items: Array<{ label: string; icon?: React.ReactNode; onClick?: () => void }> = [
      {
        label: t('navigation.dashboard', 'Dashboard'),
        icon: <Home size={14} />,
        onClick: () => navigate(dashPath),
      },
      {
        label: t('navigation.storeVisit', 'Store Visit'),
        icon: <Store size={14} />,
        onClick: () => navigate(storeVisitListPath),
      },
      {
        label: customerCrumbLabel,
        icon: <Store size={14} />,
        onClick: storeId ? goStoreNotes : undefined,
      },
      {
        label: t('orderPreview.title'),
        icon: <FileText size={14} />,
      },
    ];
    return items.map((item, index) => ({
      ...item,
      isActive: index === items.length - 1,
      onClick: index === items.length - 1 ? undefined : item.onClick,
    }));
  }, [
    userRole,
    storeId,
    t,
    navigate,
    onExitPreviewToStoreVisit,
    displayOrderData.customerNumber,
  ]);
  
  // Initialize edited order data when entering edit mode
  useEffect(() => {
    if (isManager && isEditMode && !editedOrderData) {
      setEditedOrderData({ ...(finalizedOrderSnapshot || orderData) });
    }
  }, [isManager, isEditMode, finalizedOrderSnapshot, orderData, editedOrderData]);
  
  // Clear signature canvas on mount and whenever order is not finalized
  useEffect(() => {
    if (signatureCanvasRef.current) {
      try {
        const canvas = signatureCanvasRef.current.getCanvas();
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
        }
      } catch (error) {
        console.error('Error clearing signature canvas:', error);
      }
    }
  }, []); // Clear on mount
  
  // Load signature from orderData when component mounts or orderData changes
  // Only load signature if the order is finalized, otherwise clear it
  useEffect(() => {
    if (finalizeSuccess && displayOrderData.customerSignature && signatureCanvasRef.current) {
      try {
        const img = new Image();
        img.src = displayOrderData.customerSignature;
        img.onload = () => {
          if (signatureCanvasRef.current) {
            try {
              const canvas = signatureCanvasRef.current.getCanvas();
              if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                }
              }
            } catch (error) {
              console.error('Error loading signature to canvas:', error);
            }
          }
        };
        img.onerror = () => {
          console.error('Error loading signature image');
        };
      } catch (error) {
        console.error('Error setting up signature image:', error);
      }
    } else if (signatureCanvasRef.current && !finalizeSuccess) {
      // Clear canvas if order is not finalized
      try {
        const canvas = signatureCanvasRef.current.getCanvas();
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
        }
      } catch (error) {
        console.error('Error clearing signature canvas:', error);
      }
    }
  }, [displayOrderData.customerSignature, finalizeSuccess]);

  // Handler for manager to save edited order
  const handleSaveOrder = async () => {
    if (!editedOrderData || !storeId) {
      setErrorMessage('Cannot save order: missing data');
      setSubmitStatus('error');
      return;
    }

    setIsSaving(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Update order data using the global order data context
      // This will update the StoreOrders document in Firestore
      await updateOrderData(editedOrderData);
      
      // Update the finalized snapshot if it exists
      if (finalizedOrderSnapshot) {
        setFinalizedOrderSnapshot({ ...editedOrderData });
      }
      
      // Exit edit mode
      setIsEditMode(false);
      setSubmitStatus('success');
      setErrorMessage('');
      
      console.log('✅ Order updated successfully');
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save order');
      console.error('Error saving order:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handler to cancel edit mode
  const handleCancelEdit = () => {
    setEditedOrderData(null);
    setIsEditMode(false);
  };

  // Email field starts empty - user must manually enter email address

  // Create hash for product items to track changes (similar to GlobalOrderSummary)
  const productItemsHash = React.useMemo(() => {
    const items = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    if (items.length === 0) return '';
    return items
      .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
      .map(item => `${item.id}:${item.quantity}`)
      .join('|');
  }, [orderData.heritageItems, orderData.livingstonItems, orderData.flowerItems, orderData.vegetableItems]);
  
  // Filter items based on orderType
  // Always use current orderData for items when not finalized
  const filteredOrderData = React.useMemo(() => {
    // If order is finalized, use finalizedOrderSnapshot, otherwise use current orderData
    // This ensures we always see the current items when not finalized
    const sourceData = (finalizeSuccess && finalizedOrderSnapshot) ? finalizedOrderSnapshot : orderData;
    
    if (orderType === 'supplies') {
      return {
        ...sourceData,
        heritageItems: [],
        livingstonItems: [],
        flowerItems: [],
        vegetableItems: []
      };
    } else {
      return {
        ...sourceData,
        supplyItems: []
      };
    }
  }, [orderData, productItemsHash, finalizeSuccess, finalizedOrderSnapshot, orderType]);

  const totalAmount = React.useMemo(() => {
    const allItems = [
      ...(filteredOrderData.heritageItems || []),
      ...(filteredOrderData.livingstonItems || []),
      ...(filteredOrderData.flowerItems || []),
      ...(filteredOrderData.vegetableItems || []),
      ...(filteredOrderData.supplyItems || [])
    ];
    return allItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [filteredOrderData]);

  const totalItems = React.useMemo(() => {
    const allItems = [
      ...(filteredOrderData.heritageItems || []),
      ...(filteredOrderData.livingstonItems || []),
      ...(filteredOrderData.flowerItems || []),
      ...(filteredOrderData.vegetableItems || []),
      ...(filteredOrderData.supplyItems || [])
    ];
    return allItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [filteredOrderData]);

  const handleFinalSubmit = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter an email address');
      setSubmitStatus('error');
      return;
    }

    if (!email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setSubmitStatus('error');
      return;
    }

    // Order must be finalized before sending email
    if (!finalizeSuccess) {
      setErrorMessage('Please finalize the order before sending email');
      setSubmitStatus('error');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitStatus('idle');
      setErrorMessage('');
      
      // Initialize EmailService
      EmailService.initialize();
      
      // Use finalized order snapshot if available, otherwise use current orderData
      // Use filteredOrderData for PDF generation to only include relevant items
      const orderDataForPDF = finalizedOrderSnapshot || filteredOrderData;
      const orderIdForPDF = orderDataForPDF.orderId || `order_${Date.now()}`;
      
      // Generate PDF and send email using the finalized order data
      // Pass translation function to PDFService
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData: orderDataForPDF,
        orderId: orderIdForPDF,
        customerEmail: email,
        t: t // Pass translation function
      });
      
      // Send email using EmailService
      // Pass translation function to EmailService
      await EmailService.sendOrderConfirmation(
        email,
        pdfBlob,
        orderIdForPDF,
        orderDataForPDF,
        t // Pass translation function
      );
      
      setSubmitStatus('success');
      console.log('✅ Email sent successfully');
      
      // Call the parent's onFinalSubmit for any additional handling
      await onFinalSubmit(email);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send email');
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Use finalized order snapshot if available, otherwise use current orderData
      // Use filteredOrderData for PDF generation to only include relevant items
      const orderDataForPDF = finalizedOrderSnapshot || filteredOrderData;
      const orderIdForPDF = orderDataForPDF.orderId || `order_${Date.now()}`;
      
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData: orderDataForPDF,
        orderId: orderIdForPDF,
        customerEmail: email,
        t: t // Pass translation function to ensure correct language
      });
      
      const filename = `order_${orderIdForPDF || Date.now()}.pdf`;
      PDFService.downloadPDF(pdfBlob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Failed to generate PDF');
      setSubmitStatus('error');
    }
  };

  const handleFinalizeOrder = async () => {
    // Use displayOrderData for validation (may be current orderData or snapshot)
    if (!storeId || !displayOrderData.orderId || !user?.id) {
      setErrorMessage('Missing required information to finalize order');
      setSubmitStatus('error');
      return;
    }

    if (totalItems === 0) {
      setErrorMessage('Cannot finalize an empty order');
      setSubmitStatus('error');
      return;
    }

    setIsFinalizing(true);
    setFinalizeSuccess(false);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Persist current order data (including customerSignature) to StoreOrders draft
      // so that when finalizeOrder reads the draft it gets the signature and saves it to OrderHistory
      await saveOrderData();

      const result = await finalizationService.finalizeOrder(
        storeId,
        displayOrderData.orderId,
        user.id
      );

      if (result.success) {
        console.log('✅ Order finalized successfully:', result.finalOrderId);
        onOrderFinalizedSuccess?.();
        setFinalizeSuccess(true);
        setSubmitStatus('success');
        
        // Save a snapshot of the finalized order data for static preview
        // This ensures the preview screen continues to show the finalized order
        // even after clearing the global order data
        // Create a deep copy to prevent reference issues
        // Use filteredOrderData to only save relevant items based on orderType
        const finalizedSnapshot = {
          ...filteredOrderData,
          orderId: result.finalOrderId || displayOrderData.orderId,
          // Deep copy arrays to prevent reference issues
          heritageItems: filteredOrderData.heritageItems ? [...filteredOrderData.heritageItems] : [],
          livingstonItems: filteredOrderData.livingstonItems ? [...filteredOrderData.livingstonItems] : [],
          flowerItems: filteredOrderData.flowerItems ? [...filteredOrderData.flowerItems] : [],
          vegetableItems: filteredOrderData.vegetableItems ? [...filteredOrderData.vegetableItems] : [],
          supplyItems: filteredOrderData.supplyItems ? [...filteredOrderData.supplyItems] : [],
          customerSignature: displayOrderData.customerSignature
        };
        setFinalizedOrderSnapshot(finalizedSnapshot);
        
        // Notify portal ops when order is finalized (CC only — not customer service).
        // Customer service only receives email when a manager explicitly clicks "Send to Customer Service".
        try {
          EmailService.initialize();
          const orderIdForEmail = result.finalOrderId || displayOrderData.orderId;
          const customerEmail = displayOrderData.email || '';
          
          const pdfBlob = await PDFService.generateOrderPDF({
            orderData: finalizedSnapshot,
            orderId: orderIdForEmail,
            customerEmail: customerEmail,
            t: t
          });
          
          // Sends to EMAIL_ADDRESSES.portalOpsCc only — does NOT email customer service
          await EmailService.sendOrderToCustomerService(
            pdfBlob,
            orderIdForEmail,
            finalizedSnapshot,
            t
          );
          
          console.log('✅ Portal ops notified of new finalized order');
        } catch (emailError) {
          // Non-blocking — don't fail finalization if notification email fails
          console.error('⚠️ Failed to send portal ops notification email:', emailError);
        }
        
        // Clear product selections from global order data after finalization
        // StoreOrders document is already deleted by finalizationService
        // Keep order information fields for email sending, but clear products based on orderType
        // Note: This does NOT affect the preview screen which uses the snapshot
        setTimeout(async () => {
          try {
            // Clear only the relevant items based on orderType
            // The preview screen will continue using finalizedOrderSnapshot
            if (orderType === 'supplies') {
              // For supplies orders, only clear supply items
              updateOrderData({
                supplyItems: []
              });
            } else {
              // For store orders, only clear product items (keep supplies)
              updateOrderData({
                heritageItems: [],
                livingstonItems: [],
                flowerItems: [],
                vegetableItems: []
              });
            }
            
            // Clear draft cache to remove product selections
            await clearDraft();
            
            console.log('🧹 Products cleared from global order data after finalization');
            console.log('✅ Preview screen will continue showing finalized order snapshot');
          } catch (error) {
            console.error('Error clearing products:', error);
            // Still clear products even if cache clear fails
            if (orderType === 'supplies') {
              updateOrderData({
                supplyItems: []
              });
            } else {
              updateOrderData({
                heritageItems: [],
                livingstonItems: [],
                flowerItems: [],
                vegetableItems: []
              });
            }
          }
        }, 1000); // Small delay to ensure finalization completes
      } else {
        console.error('Failed to finalize order:', result.error);
        setErrorMessage(`Failed to finalize order: ${result.error}`);
        setSubmitStatus('error');
      }
    } catch (error: any) {
      console.error('Error finalizing order:', error);
      setErrorMessage('Failed to finalize order');
      setSubmitStatus('error');
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <PreviewContainer>
      <div style={{ marginBottom: Spacing.md }}>
        <Breadcrumb items={previewBreadcrumbItems} />
      </div>

      {/* Preview Header */}
      <PreviewHeader>
        <FileText size={40} color="white" />
        <PreviewHeaderContent>
          <PreviewTitle>{t('orderPreview.title')}</PreviewTitle>
          <PreviewSubtitle>{t('orderPreview.subtitle')}</PreviewSubtitle>
        </PreviewHeaderContent>
      </PreviewHeader>

      <PDFPreviewContainer>
        <PDFHeader>
          <FileText size={20} />
          <span>{t('forms.orderSummary')} - {displayOrderData.storeName || t('email.storeName')}</span>
        </PDFHeader>
        
        <PDFContent>
          <StaticPDFPreview>
            {/* Header with Logo and Company Info */}
            <PDFPreviewHeader>
              <PDFPreviewLogo>NEXIS</PDFPreviewLogo>
              <PDFPreviewCompanyInfo>
                <div><strong>Nexis ERP</strong></div>
                <div>Field operations & supply chain</div>
              </PDFPreviewCompanyInfo>
            </PDFPreviewHeader>

            {/* Title */}
            <PDFPreviewTitle>{t('pdf.orderConfirmation')}</PDFPreviewTitle>
            
            <PDFDateInfo>
              <div><strong>{t('pdf.poNumber')}:</strong> {displayOrderData.poNumber || t('pdf.na')}</div>
              <div><strong>{t('pdf.date')}:</strong> {new Date().toLocaleDateString()}</div>
            </PDFDateInfo>

            {/* Customer and Order Information (Two Columns) */}
            <PDFPreviewTwoColumn>
              {/* Customer Information */}
              <PDFPreviewSection>
                <PDFPreviewSectionTitle>{t('pdf.customerInformation')}</PDFPreviewSectionTitle>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.customerHash')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.customerNumber || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, customerNumber: e.target.value })}
                    />
                  ) : (
                    displayOrderData.customerNumber || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.storeName')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.storeName || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, storeName: e.target.value })}
                    />
                  ) : (
                    displayOrderData.storeName || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.address')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.address || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, address: e.target.value })}
                    />
                  ) : (
                    displayOrderData.address || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.city')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.city || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, city: e.target.value })}
                    />
                  ) : (
                    displayOrderData.city || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.province')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.province || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, province: e.target.value })}
                    />
                  ) : (
                    displayOrderData.province || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.postalCode')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.postalCode || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, postalCode: e.target.value })}
                    />
                  ) : (
                    displayOrderData.postalCode || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.email')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="email"
                      value={editedOrderData.email || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, email: e.target.value })}
                    />
                  ) : (
                    displayOrderData.email || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.phone')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="tel"
                      value={editedOrderData.phoneNumber || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, phoneNumber: e.target.value })}
                    />
                  ) : (
                    displayOrderData.phoneNumber || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
              </PDFPreviewSection>

              {/* Order Information */}
              <PDFPreviewSection>
                <PDFPreviewSectionTitle>{t('pdf.orderInformation')}</PDFPreviewSectionTitle>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.poNumber')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="text"
                      value={editedOrderData.poNumber || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, poNumber: e.target.value })}
                    />
                  ) : (
                    displayOrderData.poNumber || t('pdf.na')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.orderDate')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="date"
                      value={editedOrderData.orderDate || getLocalCalendarYMD()}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, orderDate: e.target.value })}
                    />
                  ) : (
                    displayOrderData.orderDate
                      ? formatOrderDateForDisplay(displayOrderData.orderDate)
                      : formatOrderDateForDisplay(new Date())
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.deliveryDate')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableInput
                      type="date"
                      value={editedOrderData.deliveryDate || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, deliveryDate: e.target.value })}
                    />
                  ) : (
                    displayOrderData.deliveryDate || t('pdf.tbd')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.orderType')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableSelect
                      value={editedOrderData.orderType || 'standardOrder'}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, orderType: e.target.value as any })}
                    >
                      <option value="standardOrder">{t('pdf.standardOrder')}</option>
                      <option value="custOrder">{t('pdf.custOrder')}</option>
                      <option value="offCarDelivery">{t('pdf.offCarDelivery')}</option>
                    </EditableSelect>
                  ) : (
                    displayOrderData.orderType === 'custOrder' ? t('pdf.custOrder') : 
                    displayOrderData.orderType === 'offCarDelivery' ? t('pdf.offCarDelivery') : t('pdf.standardOrder')
                  )}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.specialInstructions')}:</PDFPreviewInfoLabel>
                  {isEditMode && editedOrderData ? (
                    <EditableTextArea
                      value={editedOrderData.specialInstructions || ''}
                      onChange={(e) => setEditedOrderData({ ...editedOrderData, specialInstructions: e.target.value })}
                      placeholder={t('pdf.none')}
                    />
                  ) : (
                    displayOrderData.specialInstructions || t('pdf.none')
                  )}
                </PDFPreviewInfoItem>
              </PDFPreviewSection>
            </PDFPreviewTwoColumn>

            {/* Product Selections */}
            <PDFPreviewSection>
              <PDFPreviewSectionTitle>{t('pdf.productSelections')}</PDFPreviewSectionTitle>
              
              {(() => {
                // Use filteredOrderData to only show relevant items based on orderType
                const heritageItems = filteredOrderData.heritageItems || [];
                const livingstonItems = filteredOrderData.livingstonItems || [];
                const flowerItems = filteredOrderData.flowerItems || [];
                const vegetableItems = filteredOrderData.vegetableItems || [];
                const supplyItems = filteredOrderData.supplyItems || [];
                
                const formSections = [
                  { items: heritageItems, name: t('categories.heritage'), category: 'H' },
                  { items: livingstonItems, name: 'Livingston', category: 'L' },
                  { items: flowerItems, name: t('categories.flower'), category: 'F' },
                  { items: vegetableItems, name: t('categories.vegetable'), category: 'V' },
                  { items: supplyItems, name: t('categories.supply', 'Supplies'), category: 'S' }
                ].filter(section => section.items.length > 0);

                if (formSections.length === 0) {
                  return <p style={{ fontStyle: 'italic', color: ThemeColors.text.secondary }}>{t('pdf.noProductsSelected')}</p>;
                }

                return formSections.map((section, sectionIndex) => {
                  // Use editedOrderData items if in edit mode, otherwise use displayOrderData items
                  const sectionItems = isEditMode && editedOrderData 
                    ? (section.category === 'H' ? editedOrderData.heritageItems :
                       section.category === 'L' ? editedOrderData.livingstonItems :
                       section.category === 'F' ? editedOrderData.flowerItems :
                       section.category === 'S' ? editedOrderData.supplyItems : editedOrderData.vegetableItems) || []
                    : section.items;
                  
                  const sectionTotal = sectionItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                  const sectionItemCount = sectionItems.reduce((sum, item) => sum + item.quantity, 0);
                  const itemLabel = sectionItemCount === 1 ? t('pdf.item') : t('pdf.items');

                  return (
                    <ProductSectionContainer key={sectionIndex}>
                      <ProductSectionTitle>
                        {section.name} {t('pdf.products')}:
                      </ProductSectionTitle>
                      <PDFPreviewTable>
                        <PDFPreviewTableHeader>
                          <tr>
                            <th>{t('pdf.product')}</th>
                            <th>{t('pdf.qty')}</th>
                            <th>{t('pdf.price')}</th>
                            <th>{t('pdf.total')}</th>
                          </tr>
                        </PDFPreviewTableHeader>
                        <PDFPreviewTableBody>
                          {sectionItems.map((item, itemIndex) => {
                            // Translate product name (stored in English, displayed translated)
                            const translatedProductName = getTranslatedProductName(item.packetDescription || '', t);
                            const itemArrayName = section.category === 'H' ? 'heritageItems' : 
                                                  section.category === 'L' ? 'livingstonItems' :
                                                  section.category === 'F' ? 'flowerItems' :
                                                  section.category === 'S' ? 'supplyItems' : 'vegetableItems';
                            
                            return (
                              <tr key={itemIndex}>
                                <td>
                                  <span style={{ fontWeight: Typography.fontWeight.bold }}>[{section.category}]</span>{' '}
                                  {translatedProductName}
                                  {item.code && <span style={{ fontSize: '0.85em', color: ThemeColors.text.secondary }}> ({t('pdf.code')}: {item.code})</span>}
                                </td>
                                <td>
                                  {isEditMode && editedOrderData ? (
                                    <EditableQuantityInput
                                      type="number"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      min="0"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newQuantity = parseInt(e.target.value) || 0;
                                        const updatedItems = [...(editedOrderData[itemArrayName as keyof typeof editedOrderData] as any[] || [])];
                                        updatedItems[itemIndex] = { ...item, quantity: newQuantity };
                                        setEditedOrderData({
                                          ...editedOrderData,
                                          [itemArrayName]: updatedItems
                                        });
                                      }}
                                    />
                                  ) : (
                                    item.quantity
                                  )}
                                </td>
                                <td>${item.price.toFixed(2)}</td>
                                <td>${(item.price * item.quantity).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </PDFPreviewTableBody>
                      </PDFPreviewTable>
                      <div style={{ textAlign: 'right', marginBottom: Spacing.md, fontWeight: Typography.fontWeight.bold }}>
                        {section.name} {t('pdf.subtotal')} ({sectionItemCount} {itemLabel}): ${sectionTotal.toFixed(2)}
                      </div>
                    </ProductSectionContainer>
                  );
                });
              })()}

              {/* Total */}
              <PDFPreviewTotalSection>
                <div>{t('pdf.total')} ({totalItems} {totalItems === 1 ? t('pdf.item') : t('pdf.items')})</div>
                <div>${totalAmount.toFixed(2)}</div>
              </PDFPreviewTotalSection>
            </PDFPreviewSection>
          </StaticPDFPreview>
        </PDFContent>
      </PDFPreviewContainer>

      {/* Customer Signature Section */}
      <SignatureSection>
        <SignatureTitle>
          <PenTool size={20} />
          {t('orderPreview.customerSignature', 'Customer Signature')}
        </SignatureTitle>
        <SignatureContainer>
          <SignatureCanvasContainer 
            ref={signatureContainerRef}
            disabled={!!(finalizeSuccess && displayOrderData.customerSignature)}
          >
            <SignatureCanvas
              ref={signatureCanvasRef}
              canvasProps={{
                className: 'signature-canvas',
                style: (finalizeSuccess && displayOrderData.customerSignature) ? { pointerEvents: 'none' } : {}
              }}
              backgroundColor="white"
              penColor="black"
            />
          </SignatureCanvasContainer>
          {!(finalizeSuccess && displayOrderData.customerSignature) && (
            <SignatureButtons>
              <SignatureButton
                variant="secondary"
                onClick={() => {
                  try {
                    if (signatureCanvasRef.current) {
                      signatureCanvasRef.current.clear();
                      updateOrderData({ customerSignature: undefined });
                    }
                  } catch (error) {
                    console.error('Error clearing signature:', error);
                  }
                }}
              >
                <Trash2 size={16} />
                {t('orderPreview.clearSignature', 'Clear')}
              </SignatureButton>
              <SignatureButton
                variant="primary"
                onClick={() => {
                  try {
                    if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
                      const signatureData = signatureCanvasRef.current.toDataURL('image/png');
                      updateOrderData({ customerSignature: signatureData });
                    }
                  } catch (error) {
                    console.error('Error saving signature:', error);
                  }
                }}
              >
                <Save size={16} />
                {t('orderPreview.saveSignature', 'Save Signature')}
              </SignatureButton>
            </SignatureButtons>
          )}
          {finalizeSuccess && displayOrderData.customerSignature && (
            <div style={{ 
              marginTop: Spacing.sm, 
              padding: Spacing.sm, 
              background: ThemeColors.success.light, 
              borderRadius: BorderRadius.sm,
              textAlign: 'center',
              color: ThemeColors.success.dark,
              fontSize: Typography.fontSize.sm
            }}>
              <CheckCircle size={16} style={{ display: 'inline', marginRight: Spacing.xs }} />
              {t('orderPreview.signatureSaved', 'Signature saved')}
            </div>
          )}
          {!(finalizeSuccess && displayOrderData.customerSignature) && (
            <SignatureNote>
              {t('orderPreview.signatureNote', 'Please sign above to add your signature to the order PDF. This is optional.')}
            </SignatureNote>
          )}
        </SignatureContainer>
      </SignatureSection>

      {/* Email Section */}
      <EmailSection>
        <EmailLabel htmlFor="email">
          <Mail size={20} />
          {t('orderPreview.sendCopyToEmail')}
        </EmailLabel>
        <EmailInput
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            const newEmail = e.target.value;
            setEmail(newEmail);
            // Update email in StoreOrders document when edited
            // This ensures the email is saved even if user doesn't send email
            if (newEmail && newEmail.includes('@')) {
              updateOrderData({ email: newEmail });
            }
          }}
          placeholder={t('orderPreview.enterEmail')}
          required
        />
        <p style={{ 
          fontSize: Typography.fontSize.sm, 
          color: ThemeColors.text.secondary, 
          margin: 0,
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {t('orderPreview.emailCopyNote')}
        </p>
      </EmailSection>

      {/* Status Messages */}
      {finalizeSuccess && submitStatus === 'idle' && (
        <StatusMessage type="success">
          <CheckCircle size={20} />
          Order finalized successfully! You can now send the email.
        </StatusMessage>
      )}
      
      {finalizeSuccess && submitStatus === 'success' && (
        <StatusMessage type="success">
          <CheckCircle size={20} />
          Email sent successfully!
        </StatusMessage>
      )}
      
      {submitStatus === 'error' && (
        <StatusMessage type="error">
          <AlertCircle size={20} />
          {errorMessage}
        </StatusMessage>
      )}

      {/* Action Buttons */}
      <ButtonContainer>
        {/* Manager Edit/Save buttons */}
        {isManager && !isEditMode && (
          <Button 
            variant="secondary" 
            onClick={() => setIsEditMode(true)}
          >
            <Edit size={20} />
            Edit Order
          </Button>
        )}
        
        {isManager && isEditMode && (
          <>
            <Button 
              variant="primary" 
              onClick={handleSaveOrder}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div style={{ width: 20, height: 20, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Changes
                </>
              )}
            </Button>
            
            <Button 
              variant="secondary" 
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <X size={20} />
              Cancel
            </Button>
          </>
        )}
        
        {isManager && (
          <Button
            variant="secondary"
            onClick={() => {
              if (finalizeSuccess) {
                if (onExitPreviewToStoreVisit) {
                  onExitPreviewToStoreVisit();
                } else if (storeId) {
                  navigate(`/manager/store-details/${storeId}?tab=store-visit`);
                } else {
                  onBack();
                }
              } else {
                onBack();
              }
            }}
          >
            <ArrowLeft size={20} />
            {finalizeSuccess
              ? t('orderPreview.backToStoreVisit', 'Back to store visit')
              : t('orderPreview.backToEdit')}
          </Button>
        )}

        {!isManager && (
          <Button 
            variant="secondary" 
            onClick={() => {
              // For sales reps, when order is finalized, navigate to store page with store-visit tab
              if (userRole === 'sales-representative' && finalizeSuccess) {
                if (storeId) {
                  console.log('Navigating to store page:', `/store-details/${storeId}?tab=store-visit`);
                  // Navigate to the store details page with store-visit tab (notes page)
                  window.location.href = `/store-details/${storeId}?tab=store-visit`;
                } else {
                  console.warn('Cannot navigate: storeId is missing. storeId prop:', storeId);
                  onBack();
                }
              } else {
                onBack();
              }
            }}
          >
            <ArrowLeft size={20} />
            {userRole === 'sales-representative' && finalizeSuccess 
              ? (t('orderPreview.backToStore') || 'Back to Store')
              : t('orderPreview.backToEdit')}
          </Button>
        )}
        
        {/* Only show download PDF button after order is finalized */}
        {finalizeSuccess && (
        <Button variant="secondary" onClick={handleDownloadPDF}>
          <Download size={20} />
          {t('orderPreview.downloadPDF')}
        </Button>
        )}
        
        {/* Finalize Order Button */}
        {!finalizeSuccess && (
          <Button 
            variant="primary" 
            onClick={handleFinalizeOrder}
            disabled={isFinalizing || totalItems === 0}
            style={{ 
              background: ThemeColors.success.main,
              borderColor: ThemeColors.success.main
            }}
          >
            {isFinalizing ? (
              <>
                <div style={{ width: 20, height: 20, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Finalizing Order...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Finalize Order
              </>
            )}
          </Button>
        )}
        
        <Button 
          variant="primary" 
          onClick={handleFinalSubmit}
          disabled={isSubmitting || !finalizeSuccess || totalItems === 0}
        >
          {isSubmitting ? (
            <>
              <div style={{ width: 20, height: 20, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              {t('orderPreview.loading')}
            </>
          ) : (
            <>
              <Send size={20} />
              {t('orderPreview.sendEmail')}
            </>
          )}
        </Button>
      </ButtonContainer>
    </PreviewContainer>
  );
};
